import axios, { InternalAxiosRequestConfig } from 'axios';

/**
 * Origin of the Klarity API.
 * Override per environment with REACT_APP_API_URL, e.g.
 *   REACT_APP_API_URL=https://backend.klaritylb.com
 */
export const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:4000').replace(/\/+$/, '');

/**
 * Products store images as server paths ("/uploads/klarity-product.jpg").
 * Turn those into absolute URLs; leave absolute URLs and local /img assets alone.
 */
export const resolveImage = (path?: string): string => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path) || path.startsWith('data:')) return path;
  if (path.startsWith('/img/')) return path;
  return `${API_ORIGIN}${path.startsWith('/') ? '' : '/'}${path}`;
};

/** Pull the readable message out of an axios error, whatever the shape. */
export const apiError = (err: any, fallback: string): Error =>
  new Error(err?.response?.data?.error || err?.message || fallback);

const axiosInstance = axios.create({
  baseURL: `${API_ORIGIN}/api`,
  withCredentials: true,
});

// Access token is only needed for the admin/account routes we add later,
// but attaching it here keeps every future call authenticated for free.
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

/**
 * Access tokens live 15 minutes; the refresh cookie lives 7 days.
 * One in-flight refresh is shared, so a burst of calls expiring together
 * (an image upload followed by the product save) only refreshes once.
 */
let refreshInFlight: Promise<string> | null = null;

const refreshAccessToken = (): Promise<string> => {
  if (!refreshInFlight) {
    // Bare axios, not the instance — going through it would recurse into this
    // very interceptor when the refresh itself is rejected.
    refreshInFlight = axios
      .post(`${API_ORIGIN}/api/auth/refresh`, null, { withCredentials: true })
      .then((res) => {
        const token = res.data?.accessToken;
        if (!token) throw new Error('Refresh returned no access token');
        localStorage.setItem('accessToken', token);
        return token;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
};

// The API answers an expired token with 403 "Forbidden. Invalid token." —
// swap in a fresh one and replay the call rather than surfacing that to the admin.
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config as RetriableConfig | undefined;
    const status = error?.response?.status;
    const message = error?.response?.data?.error || '';

    const expiredToken = status === 401 || status === 403;
    // A signed-in client hitting an admin route is a real 403, not a stale token.
    const notAnAdmin = message === 'Admins only';
    // Login / refresh failures are the credentials being wrong; never replay those.
    const isAuthCall = original?.url?.includes('/auth/');

    if (!original || original._retry || !expiredToken || notAnAdmin || isAuthCall) {
      return Promise.reject(error);
    }

    original._retry = true;

    try {
      const token = await refreshAccessToken();
      if (original.headers) {
        original.headers.Authorization = `Bearer ${token}`;
      }
      return await axiosInstance(original);
    } catch {
      // The refresh cookie is gone or expired too — this session is over.
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/signin') {
        window.location.assign('/signin');
      }
      return Promise.reject(error);
    }
  }
);

export default axiosInstance;
